import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Button } from "@/components/base/buttons/button";
import {
    hasShipperAskEvePromptForOnboarding,
    hasShipperDocumentViewedForOnboarding,
    isTodoDone,
    setTodoOverride,
    type ShipperOnboardingTodoId,
} from "@/lib/shipper-onboarding-storage";
import { useChatAI } from "@/components/application/ai-chat/chat-ai-context";

const TODO_IDS: ShipperOnboardingTodoId[] = ["email", "profile", "container", "document", "askEve"];

export type ShipperControlHubOnboardingTodosProps = {
    autoEmailConnected: boolean;
    autoProfileComplete: boolean;
    autoHasContainer: boolean;
    variant?: "banner" | "card";
    onVisibleChange?: (visible: boolean) => void;
};

export function ShipperControlHubOnboardingTodos({
    autoEmailConnected,
    autoProfileComplete,
    autoHasContainer,
    variant = "card",
    onVisibleChange,
}: ShipperControlHubOnboardingTodosProps) {
    const { t } = useTranslation(["pages:dashboard", "common"]);
    const { openAskEvePanel } = useChatAI();
    const [storageTick, setStorageTick] = useState(0);

    const refresh = useCallback(() => setStorageTick((n) => n + 1), []);

    useEffect(() => {
        const onChange = () => refresh();
        window.addEventListener("shipper-onboarding-changed", onChange);
        return () => window.removeEventListener("shipper-onboarding-changed", onChange);
    }, [refresh]);

    const autoFlags = useMemo(
        () => ({
            email: autoEmailConnected,
            profile: autoProfileComplete,
            container: autoHasContainer,
            document: hasShipperDocumentViewedForOnboarding(),
            askEve: hasShipperAskEvePromptForOnboarding(),
        }),
        [autoEmailConnected, autoProfileComplete, autoHasContainer, storageTick]
    );

    const doneStates = useMemo(() => {
        const out: Record<ShipperOnboardingTodoId, boolean> = {} as Record<ShipperOnboardingTodoId, boolean>;
        for (const id of TODO_IDS) {
            out[id] = isTodoDone(id, autoFlags[id]);
        }
        return out;
    }, [autoFlags]);

    const doneCount = TODO_IDS.filter((id) => doneStates[id]).length;
    const total = TODO_IDS.length;
    const allDone = doneCount === total;
    const pct = Math.round((doneCount / total) * 100);

    useLayoutEffect(() => {
        onVisibleChange?.(!allDone);
    }, [allDone, onVisibleChange]);

    const onToggle = (id: ShipperOnboardingTodoId, selected: boolean) => {
        setTodoOverride(id, selected);
        refresh();
    };

    if (allDone) return null;

    const actionHref: Record<Exclude<ShipperOnboardingTodoId, "askEve">, string> = {
        email: "/shipper/profile?tab=integrations",
        profile: "/shipper/profile?tab=companies",
        container: "/shipper/containers/new",
        document: "/shipper/containers",
    };

    const isCard = variant === "card";
    const sectionClass = isCard
        ? "flex min-h-0 flex-col rounded-xl bg-primary p-5 shadow-xs ring-1 ring-secondary"
        : "w-full rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary";

    return (
        <section
            className={sectionClass}
            aria-labelledby="shipper-onboarding-heading"
            aria-describedby="shipper-onboarding-progress"
        >
            <div
                className={
                    isCard
                        ? "flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
                        : "flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
                }
            >
                <div>
                    <h2 id="shipper-onboarding-heading" className="text-sm font-semibold text-primary">
                        {t("controlHub.onboarding.sectionTitle")}
                    </h2>
                    <p className={`text-sm text-tertiary ${isCard ? "mt-0.5 line-clamp-2" : "mt-0.5"}`}>
                        {t("controlHub.onboarding.sectionSubtitle")}
                    </p>
                </div>
                <div
                    id="shipper-onboarding-progress"
                    className="text-sm font-medium text-secondary tabular-nums shrink-0"
                    aria-live="polite"
                >
                    {t("controlHub.onboarding.progressLabel", { pct })}
                </div>
            </div>
            <div
                className={`mt-3 w-full overflow-hidden rounded-full bg-secondary ${isCard ? "h-1.5" : "h-2"}`}
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t("controlHub.onboarding.progressLabel", { pct })}
            >
                <div className="h-full rounded-full bg-brand-solid transition-[width]" style={{ width: `${pct}%` }} />
            </div>
            <ul
                className={
                    isCard
                        ? "mt-3 max-h-[min(55vh,420px)] space-y-2 overflow-y-auto pr-1"
                        : "mt-4 space-y-3"
                }
            >
                {TODO_IDS.map((id) => {
                    const done = doneStates[id];
                    const title = t(`controlHub.onboarding.todos.${id}.title`);
                    const actionLabel = t(`controlHub.onboarding.todos.${id}.action`);

                    return (
                        <li
                            key={id}
                            className={
                                isCard
                                    ? "flex flex-col gap-1.5 rounded-lg bg-secondary/60 p-2 ring-1 ring-secondary sm:flex-row sm:items-center sm:justify-between"
                                    : "flex flex-col gap-2 rounded-lg bg-secondary/60 p-3 ring-1 ring-secondary sm:flex-row sm:items-center sm:justify-between"
                            }
                        >
                            <div className="flex min-w-0 flex-1 items-start gap-2 sm:gap-3">
                                <Checkbox
                                    size="sm"
                                    isSelected={done}
                                    onChange={(sel) => onToggle(id, sel)}
                                    aria-label={t("controlHub.onboarding.markDone", { title })}
                                />
                                <span className={`text-sm font-medium ${done ? "text-tertiary line-through" : "text-primary"}`}>
                                    {title}
                                </span>
                            </div>
                            <div className="flex shrink-0 items-center gap-2 pl-7 sm:pl-0">
                                {id === "askEve" ? (
                                    <Button color="secondary" size="sm" onPress={() => openAskEvePanel()}>
                                        {actionLabel}
                                    </Button>
                                ) : (
                                    <Button color="secondary" size="sm" href={actionHref[id]}>
                                        {actionLabel}
                                    </Button>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}
